import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Nav from '../components/nav';
import SEO from '../components/SEO';

const injectStyles = () => {
  if (document.getElementById('pb-dist-styles')) return;
  const s = document.createElement('style');
  s.id = 'pb-dist-styles';
  s.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Space+Mono:wght@400;700&family=DM+Sans:wght@300;400;500&display=swap');
    .pb-dist * { box-sizing:border-box; margin:0; padding:0; }
    @keyframes dist-blink    { 0%,100%{opacity:1} 50%{opacity:0} }
    @keyframes dist-fadein   { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:translateY(0)} }
    @keyframes dist-prog     { from{width:0%} to{width:100%} }
    @keyframes dist-pulse    { 0%{transform:scale(1);opacity:.9} 50%{transform:scale(1.15);opacity:.6} 100%{transform:scale(1);opacity:.9} }
    @keyframes dist-ring     { 0%{transform:scale(1);opacity:.7} 100%{transform:scale(2.8);opacity:0} }
    @keyframes dist-flow     { 0%{stroke-dashoffset:200} 100%{stroke-dashoffset:0} }
    @keyframes dist-spin     { from{transform:rotateY(0deg)} to{transform:rotateY(360deg)} }
    @keyframes dist-orbit    { from{transform:rotate(0deg) translateX(120px) rotate(0deg)} to{transform:rotate(360deg) translateX(120px) rotate(-360deg)} }
    @keyframes dist-glow     { 0%,100%{box-shadow:0 0 8px rgba(0,255,170,.3)} 50%{box-shadow:0 0 24px rgba(0,255,170,.7),0 0 48px rgba(0,255,170,.2)} }
    @keyframes dist-travel   { 0%{opacity:0;transform:translate(0,0)} 20%{opacity:1} 80%{opacity:1} 100%{opacity:0;transform:translate(var(--tx),var(--ty))} }
    @keyframes dist-scan     { 0%,100%{opacity:.2} 50%{opacity:.6} }

    .dist-cursor { display:inline-block;width:3px;height:.85em;background:#00ffaa;vertical-align:text-bottom;margin-left:2px;animation:dist-blink .7s step-end infinite; }
    .dist-slide-enter { animation:dist-fadein .6s cubic-bezier(.16,1,.3,1) both; }
    .dist-prog-bar { animation:dist-prog linear both; }
    .dist-node-pulse { animation:dist-pulse 2s ease-in-out infinite; }
    .dist-ctrl:hover { background:rgba(0,255,170,.1)!important;color:#00ffaa!important;border-color:rgba(0,255,170,.4)!important; }
    .dist-dot-active { width:26px!important;border-radius:3px!important;background:#00ffaa!important; }
    .dist-card:hover { transform:translateY(-5px)!important;border-color:rgba(0,255,170,.35)!important; }
    .dist-ai-item:hover { background:rgba(0,255,170,.07)!important;border-color:rgba(0,255,170,.25)!important; }
    .dist-btn-p:hover { transform:translateY(-3px);box-shadow:0 12px 40px rgba(0,255,170,.3); }
    .dist-btn-g:hover { border-color:#00ffaa!important;color:#00ffaa!important; }
    .dist-channel-card:hover { transform:translateY(-4px);border-color:rgba(0,255,170,.3)!important; }
  `;
  document.head.appendChild(s);
};

// ── Animated Globe Canvas ──────────────────────────────────────────────────────
const GlobeCanvas = ({ activeNode, onNodeClick }) => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const timeRef = useRef(0);

  const NODES = [
    { id: 'center', label: 'ProducerBuddy', x: 0, y: 0, r: 18, color: '#00ffaa', ring: true, main: true },
    { id: 'ott',    label: 'OTT',        angle: 0,   dist: 140, color: '#3b82f6', r: 13 },
    { id: 'fest',   label: 'Festivals',  angle: 60,  dist: 150, color: '#a855f7', r: 11 },
    { id: 'theat',  label: 'Theatrical', angle: 120, dist: 145, color: '#f59e0b', r: 12 },
    { id: 'dig',    label: 'Digital',    angle: 190, dist: 138, color: '#ec4899', r: 11 },
    { id: 'mkt',    label: 'Marketing',  angle: 255, dist: 148, color: '#10b981', r: 11 },
    { id: 'tv',     label: 'Broadcast',  angle: 315, dist: 142, color: '#06b6d4', r: 10 },
  ];

  const getNodePos = useCallback((node, cx, cy) => {
    if (node.main) return { x: cx, y: cy };
    const rad = (node.angle * Math.PI) / 180;
    return { x: cx + Math.cos(rad) * node.dist, y: cy + Math.sin(rad) * node.dist };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width = canvas.offsetWidth;
    const H = canvas.height = canvas.offsetHeight;
    const cx = W / 2, cy = H / 2;

    // Traveling packets
    const packets = [];
    const spawnPacket = () => {
      const from = NODES[0];
      const to = NODES[Math.floor(Math.random() * (NODES.length - 1)) + 1];
      packets.push({ from: 'center', to: to.id, progress: 0, speed: 0.008 + Math.random() * 0.006, color: to.color });
    };
    let spawnTimer = setInterval(spawnPacket, 600);

    const draw = (t) => {
      timeRef.current = t;
      ctx.clearRect(0, 0, W, H);

      // Globe wireframe
      ctx.save();
      ctx.globalAlpha = 0.06;
      for (let lat = -80; lat <= 80; lat += 20) {
        const y = cy + (lat / 90) * 160;
        const xSpan = Math.sqrt(Math.max(0, 160 * 160 - (y - cy) * (y - cy)));
        ctx.beginPath();
        ctx.ellipse(cx, y, xSpan, xSpan * 0.2, 0, 0, Math.PI * 2);
        ctx.strokeStyle = '#00ffaa';
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }
      for (let lng = 0; lng < 360; lng += 30) {
        const rad = (lng * Math.PI) / 180;
        ctx.beginPath();
        ctx.ellipse(cx, cy, 160 * Math.abs(Math.cos(rad + t * 0.0003)), 160, rad * 0.1, 0, Math.PI * 2);
        ctx.strokeStyle = '#00ffaa';
        ctx.lineWidth = 0.4;
        ctx.stroke();
      }
      ctx.restore();

      // Outer globe circle
      ctx.beginPath();
      ctx.arc(cx, cy, 160, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(0,255,170,0.12)';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Connection lines
      NODES.slice(1).forEach(node => {
        const from = getNodePos(NODES[0], cx, cy);
        const to = getNodePos(node, cx, cy);
        const active = activeNode === node.id;
        const grad = ctx.createLinearGradient(from.x, from.y, to.x, to.y);
        grad.addColorStop(0, 'rgba(0,255,170,0.5)');
        grad.addColorStop(1, active ? node.color + 'cc' : node.color + '40');
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.strokeStyle = grad;
        ctx.lineWidth = active ? 2 : 0.8;
        ctx.setLineDash(active ? [] : [4, 6]);
        ctx.stroke();
        ctx.setLineDash([]);
      });

      // Traveling packets
      packets.forEach((pkt, i) => {
        const fromNode = NODES.find(n => n.id === pkt.from);
        const toNode = NODES.find(n => n.id === pkt.to);
        if (!fromNode || !toNode) return;
        const f = getNodePos(fromNode, cx, cy);
        const to = getNodePos(toNode, cx, cy);
        pkt.progress += pkt.speed;
        if (pkt.progress > 1.2) { packets.splice(i, 1); return; }
        const px = f.x + (to.x - f.x) * Math.min(pkt.progress, 1);
        const py = f.y + (to.y - f.y) * Math.min(pkt.progress, 1);
        const alpha = pkt.progress < 0.2 ? pkt.progress * 5 : pkt.progress > 0.8 ? (1 - pkt.progress) * 5 : 1;
        ctx.beginPath();
        ctx.arc(px, py, 3, 0, Math.PI * 2);
        ctx.fillStyle = pkt.color + Math.floor(alpha * 255).toString(16).padStart(2, '0');
        ctx.fill();
        // Trail
        ctx.beginPath();
        ctx.arc(px - (to.x - f.x) * 0.05, py - (to.y - f.y) * 0.05, 1.5, 0, Math.PI * 2);
        ctx.fillStyle = pkt.color + '44';
        ctx.fill();
      });

      // Draw nodes
      NODES.forEach(node => {
        const pos = getNodePos(node, cx, cy);
        const active = activeNode === node.id;
        const pulse = (Math.sin(t * 0.002 + node.angle * 0.05) + 1) * 0.5;

        if (node.ring || active) {
          ctx.beginPath();
          ctx.arc(pos.x, pos.y, node.r + 8 + pulse * 4, 0, Math.PI * 2);
          ctx.strokeStyle = node.color + '30';
          ctx.lineWidth = 1;
          ctx.stroke();
        }

        ctx.beginPath();
        ctx.arc(pos.x, pos.y, node.r, 0, Math.PI * 2);
        const fill = ctx.createRadialGradient(pos.x - node.r * 0.3, pos.y - node.r * 0.3, 0, pos.x, pos.y, node.r);
        fill.addColorStop(0, node.color + 'ee');
        fill.addColorStop(1, node.color + '66');
        ctx.fillStyle = fill;
        ctx.fill();
        ctx.strokeStyle = node.color;
        ctx.lineWidth = active ? 2.5 : 1;
        ctx.stroke();

        // Label
        ctx.font = `600 ${node.main ? 11 : 10}px 'DM Sans', sans-serif`;
        ctx.fillStyle = node.main ? '#001a0f' : '#0a0f0a';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(node.main ? 'PB' : node.label.substring(0, 4), pos.x, pos.y);

        // External label
        if (!node.main) {
          const labelDist = node.dist + node.r + 14;
          const rad = (node.angle * Math.PI) / 180;
          const lx = cx + Math.cos(rad) * labelDist;
          const ly = cy + Math.sin(rad) * labelDist;
          ctx.font = `500 10px 'DM Sans', sans-serif`;
          ctx.fillStyle = active ? node.color : node.color + 'aa';
          ctx.textAlign = lx < cx ? 'right' : lx > cx + 10 ? 'left' : 'center';
          ctx.fillText(node.label, lx, ly);
        }
      });

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(animRef.current);
      clearInterval(spawnTimer);
    };
  }, [activeNode, getNodePos]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: '100%', height: '100%', display: 'block', cursor: 'crosshair' }}
    />
  );
};

// ── Slide data ────────────────────────────────────────────────────────────────
const SLIDES = [
  {
    id: 'intro',
    kicker: 'Film Distribution Network',
    headline: 'Your film.\nEvery screen.\nEvery corner of the world.',
    body: 'ProducerBuddy\'s AI Distribution Network connects your film directly to every channel that matters — OTT giants, global film festivals, theatrical chains, digital platforms — with the intelligence of Pilomation AI managing every negotiation, submission, and deal.',
    accent: '#00ffaa',
    bg: 'radial-gradient(ellipse at 50% 50%, rgba(0,255,170,.06) 0%, transparent 60%)',
    type: 'globe',
  },
  {
    id: 'history',
    kicker: 'The old world of distribution',
    headline: 'Films died in\ncabinets.\nNot on screens.',
    body: 'For decades, distribution was the most brutal gatekeeping in the film industry. Great films sat unseen. Distributors held all the power. Independent filmmakers had no path.',
    accent: '#ef4444',
    bg: 'radial-gradient(ellipse at 30% 60%, rgba(239,68,68,.08) 0%, transparent 55%)',
    type: 'history',
  },
  {
    id: 'ott',
    kicker: 'Channel 01 — OTT Platforms',
    headline: 'One upload.\nNetflix. Prime.\nHotstar. All of them.',
    body: 'ProducerBuddy\'s AI analyzes your film\'s genre, tone, and audience fit — then submits to every relevant OTT platform simultaneously, with tailored pitches written by AI for each platform\'s editorial team.',
    accent: '#3b82f6',
    bg: 'radial-gradient(ellipse at 70% 40%, rgba(59,130,246,.09) 0%, transparent 55%)',
    type: 'channel',
    channelColor: '#3b82f6',
    items: [
      { platform: 'Netflix India', ai: 'Genre match · Audience profile · Bid analysis' },
      { platform: 'Amazon Prime Video', ai: 'Regional language AI pitch · Deal term optimizer' },
      { platform: 'Disney+ Hotstar', ai: 'IP compatibility check · Viewership predictor' },
      { platform: 'Zee5 · SonyLiv', ai: 'Content gap analysis · Release window optimizer' },
      { platform: 'MX Player · Jio Cinema', ai: 'Free vs premium tier placement AI' },
      { platform: 'YouTube Premium', ai: 'Ad revenue vs licensing model comparison' },
    ],
  },
  {
    id: 'festivals',
    kicker: 'Channel 02 — Film Festivals',
    headline: 'The right festival.\nThe right jury.\nThe right moment.',
    body: 'Festival selection is as much about timing and positioning as it is about the film. ProducerBuddy\'s AI cross-references 200+ global and Indian festivals, matching your film\'s profile to jury preferences, genre focus, and historical selection patterns.',
    accent: '#a855f7',
    bg: 'radial-gradient(ellipse at 25% 35%, rgba(168,85,247,.09) 0%, transparent 55%)',
    type: 'channel',
    channelColor: '#a855f7',
    items: [
      { platform: 'MAMI Mumbai Film Festival', ai: 'Jury preference AI · Past selection pattern analysis' },
      { platform: 'Sundance · Tribeca', ai: 'International co-production alignment scoring' },
      { platform: 'Cannes · Berlin · Venice', ai: 'A-list festival eligibility checker · Submission timing AI' },
      { platform: 'BIFF · IFFK · KIFF', ai: 'Regional festival circuit optimization' },
      { platform: 'Genre-specific festivals', ai: '200+ niche festivals matched to your film\'s DNA' },
      { platform: 'Press kit generation', ai: 'AI writes festival synopsis, director\'s note, EPK' },
    ],
  },
  {
    id: 'theatrical',
    kicker: 'Channel 03 — Theatrical Release',
    headline: 'From editing room\nto IMAX.\nAI clears the path.',
    body: 'Theatrical release was once the exclusive territory of studio-backed films. ProducerBuddy\'s theatrical distribution AI connects independent filmmakers directly to multiplex chains and single-screen networks — with data-driven release planning.',
    accent: '#f59e0b',
    bg: 'radial-gradient(ellipse at 65% 65%, rgba(245,158,11,.09) 0%, transparent 55%)',
    type: 'channel',
    channelColor: '#f59e0b',
    items: [
      { platform: 'PVR · INOX · Cinepolis', ai: 'Screen count optimizer · City-wise demand predictor' },
      { platform: 'Single-screen networks', ai: 'Regional footprint analysis · Tier-2/3 city strategy' },
      { platform: 'Release window AI', ai: 'Competes with no major release · Holiday weekend analysis' },
      { platform: 'Houseful predictor', ai: 'Pre-booking demand signals from social listening' },
      { platform: 'Censor board compliance', ai: 'AI pre-checks for certification before submission' },
      { platform: 'Print & DCP management', ai: 'Digital Cinema Package automation for all screens' },
    ],
  },
  {
    id: 'digital',
    kicker: 'Channel 04 — Digital & Social',
    headline: 'The audience is\nalready online.\nMeet them there.',
    body: 'Digital distribution isn\'t just YouTube. It\'s a coordinated campaign across every platform where your audience lives — with AI managing timing, content format, and monetization strategy across all channels simultaneously.',
    accent: '#ec4899',
    bg: 'radial-gradient(ellipse at 40% 30%, rgba(236,72,153,.08) 0%, transparent 55%)',
    type: 'channel',
    channelColor: '#ec4899',
    items: [
      { platform: 'YouTube theatrical → digital', ai: 'Window strategy · Ad revenue vs subscription model' },
      { platform: 'Instagram & Reels', ai: 'Auto-generated scene clips · Viral moment detection' },
      { platform: 'WhatsApp broadcast', ai: 'Audience segmentation · Rollout campaign sequencing' },
      { platform: 'Telegram & Discord', ai: 'Community building AI · Fan engagement automation' },
      { platform: 'Podcast & audio clips', ai: 'Dialogue extraction · Mood-based audio snippets' },
      { platform: 'Global streaming', ai: 'Territory-by-territory rights management AI' },
    ],
  },
  {
    id: 'marketing',
    kicker: 'The Marketing Enthusiast\'s role',
    headline: 'Distribution needs\nmarketers who\nspeak the language.',
    body: 'ProducerBuddy connects marketing enthusiasts directly with films that need distribution muscle. Your skills — social strategy, paid media, PR, community building — become the engine behind every successful release.',
    accent: '#10b981',
    bg: 'radial-gradient(ellipse at 55% 45%, rgba(16,185,129,.09) 0%, transparent 55%)',
    type: 'marketer',
  },
  {
    id: 'ai',
    kicker: 'Pilomation AI — The Distribution Engine',
    headline: 'Not just connections.\nIntelligence that\nnegotiates for you.',
    body: 'Every distribution decision — which platform, which timing, which deal terms — is analyzed by Pilomation\'s AI against market data, comparable releases, and real-time platform demand signals.',
    accent: '#00ffaa',
    bg: 'radial-gradient(ellipse at 50% 50%, rgba(0,255,170,.08) 0%, transparent 60%)',
    type: 'ai',
  },
  {
    id: 'cta',
    kicker: 'Your distribution network is live',
    headline: 'Stop waiting for\na distributor.\nBe your own.',
    body: 'The Pilomation distribution engine connects your film to the world. OTT deals. Festival circuits. Theatrical screens. Digital campaigns. All from one dashboard, powered by AI that never sleeps.',
    accent: '#00ffaa',
    bg: 'radial-gradient(ellipse at 50% 55%, rgba(0,255,170,.1) 0%, transparent 65%)',
    type: 'cta',
  },
];

const DURATION = 7500;
const TYPE_SPEED = 33;

const HISTORY_ROWS = [
  { stage: 'Finding a distributor', old: '6–18 months of meetings', now: 'AI matches & pitches in 24h' },
  { stage: 'OTT platform deals', old: 'Exclusive studio access only', now: 'Direct submission to all platforms' },
  { stage: 'Festival submissions', old: 'Miss deadlines, wrong festivals', now: 'AI calendar + smart matching' },
  { stage: 'Theatrical booking', old: 'Studio-backed films only', now: 'Data-driven independent release' },
  { stage: 'Revenue tracking', old: 'Quarterly statements, no visibility', now: 'Real-time dashboard + AI audit' },
  { stage: 'Rights management', old: 'Expensive entertainment lawyers', now: 'AI contract analysis included' },
];

const MARKETER_ROLES = [
  { role: 'Social Media Strategist', task: 'Build pre-release buzz campaigns', impact: '3× organic reach' },
  { role: 'Paid Media Specialist', task: 'Manage OTT & theatrical ad spend', impact: '40% lower CAC' },
  { role: 'PR & Press Coordinator', task: 'Festival press kit distribution', impact: '12× media mentions' },
  { role: 'Community Manager', task: 'Fan communities & Discord servers', impact: '10K day-1 viewers' },
  { role: 'Content Creator', task: 'Behind-the-scenes & trailer cuts', impact: '5M+ reel impressions' },
  { role: 'Data Analyst', task: 'Audience insights & performance', impact: 'Decisions in real-time' },
];

const AI_CAPABILITIES = [
  { name: 'Deal term analyzer', desc: 'Compares every offer against 500+ historical deals to flag undervalued terms' },
  { name: 'Audience geography AI', desc: 'Identifies which cities and regions will drive highest opening weekend numbers' },
  { name: 'Windowing optimizer', desc: 'Calculates exact OTT premiere timing after theatrical for maximum combined revenue' },
  { name: 'Platform fit scorer', desc: 'Scores your film\'s content DNA against each platform\'s current subscriber appetite' },
  { name: 'Rights territory AI', desc: 'Splits global rights intelligently — sells North America separately from South Asia' },
  { name: 'Revenue forecasting', desc: 'Projects 12-month revenue across all channels before you sign a single deal' },
];

const CHANNELS = [
  { id: 'ott',    label: 'OTT', color: '#3b82f6',  desc: 'Netflix · Prime · Hotstar · Zee5' },
  { id: 'fest',   label: 'Festivals', color: '#a855f7', desc: 'MAMI · Sundance · Cannes · IFFK' },
  { id: 'theat',  label: 'Theatrical', color: '#f59e0b', desc: 'PVR · INOX · Single-screen' },
  { id: 'dig',    label: 'Digital', color: '#ec4899', desc: 'YouTube · Instagram · WhatsApp' },
  { id: 'mkt',    label: 'Marketing', color: '#10b981', desc: 'Enthusiasts · Campaigns · PR' },
  { id: 'tv',     label: 'Broadcast', color: '#06b6d4', desc: 'TV channels · Pay-per-view' },
];

function useTypewriter(text, active) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    clearInterval(ref.current); setDisplayed(''); setDone(false);
    if (!active) return;
    let i = 0; const chars = text.split('');
    ref.current = setInterval(() => {
      if (i >= chars.length) { clearInterval(ref.current); setDone(true); return; }
      setDisplayed(p => p + chars[i++]);
    }, TYPE_SPEED);
    return () => clearInterval(ref.current);
  }, [text, active]);
  return { displayed, done };
}

function Headline({ text, active, accent }) {
  const { displayed, done } = useTypewriter(text, active);
  return (
    <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(36px,6vw,68px)', fontWeight: 400, lineHeight: 1.05, letterSpacing: '2px', color: '#f0fdf4', minHeight: '3.6em', marginBottom: 22 }}>
      {displayed.split('\n').map((l, i) => <span key={i} style={{ display: 'block' }}>{l}</span>)}
      {!done && <span className="dist-cursor" style={{ background: accent }} />}
    </div>
  );
}

function GlobeSlide({ visible }) {
  const [activeNode, setActiveNode] = useState(null);
  useEffect(() => {
    if (!visible) return;
    const cycle = ['ott', 'fest', 'theat', 'dig', 'mkt', 'tv', null];
    let i = 0;
    const t = setInterval(() => { setActiveNode(cycle[i % cycle.length]); i++; }, 1200);
    return () => clearInterval(t);
  }, [visible]);

  return (
    <div style={{ display: 'flex', gap: 32, alignItems: 'center', maxWidth: 880, margin: '0 auto', opacity: visible ? 1 : 0, transition: 'opacity .7s ease 1.2s' }}>
      <div style={{ width: 380, height: 380, flexShrink: 0 }}>
        <GlobeCanvas activeNode={activeNode} />
      </div>
      <div style={{ flex: 1 }}>
        {CHANNELS.map((ch, i) => (
          <div key={i} onMouseEnter={() => setActiveNode(ch.id)} onMouseLeave={() => setActiveNode(null)}
            style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
              borderRadius: 8, marginBottom: 8, cursor: 'pointer',
              background: activeNode === ch.id ? `${ch.color}12` : 'rgba(255,255,255,.03)',
              border: `1px solid ${activeNode === ch.id ? ch.color + '40' : 'rgba(255,255,255,.06)'}`,
              transition: 'all .25s',
            }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: ch.color, flexShrink: 0, animation: activeNode === ch.id ? 'dist-pulse 1s infinite' : 'none' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: activeNode === ch.id ? ch.color : '#cbd5e1', fontFamily: "'DM Sans', sans-serif" }}>{ch.label}</div>
              <div style={{ fontSize: 11, color: '#475569', fontFamily: "'Space Mono', monospace" }}>{ch.desc}</div>
            </div>
            {activeNode === ch.id && <div style={{ fontSize: 10, color: ch.color, fontFamily: "'Space Mono', monospace", letterSpacing: '1px' }}>LIVE</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function HistoryContent({ visible }) {
  return (
    <div style={{ maxWidth: 800, margin: '24px auto 0', opacity: visible ? 1 : 0, transition: 'opacity .6s ease 1.2s' }}>
      {HISTORY_ROWS.map((row, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0, borderBottom: '1px solid rgba(255,255,255,.06)', padding: '12px 0' }}>
          <div style={{ fontSize: 13, color: '#94a3b8', fontFamily: "'DM Sans', sans-serif", paddingRight: 16 }}>{row.stage}</div>
          <div style={{ fontSize: 12, color: '#ef4444', fontFamily: "'Space Mono', monospace", textDecoration: 'line-through', paddingRight: 16, opacity: .8 }}>{row.old}</div>
          <div style={{ fontSize: 12, color: '#00ffaa', fontFamily: "'Space Mono', monospace" }}>→ {row.now}</div>
        </div>
      ))}
    </div>
  );
}

function ChannelContent({ slide, visible }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12, maxWidth: 760, margin: '24px auto 0', opacity: visible ? 1 : 0, transition: 'opacity .6s ease 1.3s' }}>
      {slide.items.map((item, i) => (
        <div key={i} className="dist-card" style={{
          padding: '16px 16px', background: `${slide.channelColor}08`,
          border: `1px solid ${slide.channelColor}18`, borderRadius: 12,
          transition: 'transform .25s, border-color .25s',
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: slide.channelColor, marginBottom: 6, fontFamily: "'DM Sans', sans-serif" }}>{item.platform}</div>
          <div style={{ fontSize: 11, color: '#64748b', fontFamily: "'Space Mono', monospace", lineHeight: 1.6 }}>{item.ai}</div>
        </div>
      ))}
    </div>
  );
}

function MarketerContent({ visible }) {
  return (
    <div style={{ maxWidth: 760, margin: '24px auto 0', opacity: visible ? 1 : 0, transition: 'opacity .6s ease 1.2s' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
        {MARKETER_ROLES.map((m, i) => (
          <div key={i} style={{
            padding: '18px 14px',
            background: 'rgba(16,185,129,.06)', border: '1px solid rgba(16,185,129,.18)',
            borderRadius: 12,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#10b981', marginBottom: 6, fontFamily: "'DM Sans', sans-serif" }}>{m.role}</div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5 }}>{m.task}</div>
            <div style={{ fontSize: 11, fontFamily: "'Space Mono', monospace", color: '#00ffaa', letterSpacing: '0.5px' }}>{m.impact}</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 20, padding: '14px 18px', background: 'rgba(0,255,170,.06)', border: '1px solid rgba(0,255,170,.2)', borderRadius: 10 }}>
        <div style={{ fontSize: 12, color: '#00ffaa', fontFamily: "'Space Mono', monospace", letterSpacing: '1px', marginBottom: 4 }}>HOW IT WORKS</div>
        <div style={{ fontSize: 13, color: '#94a3b8', fontFamily: "'DM Sans', sans-serif", lineHeight: 1.6 }}>
          Production houses post their distribution brief → Marketing enthusiasts apply with their strategy → AI matches best-fit marketers to each film → Collaboration begins instantly on ProducerBuddy.
        </div>
      </div>
    </div>
  );
}

function AIContent({ visible }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12, maxWidth: 740, margin: '24px auto 0', opacity: visible ? 1 : 0, transition: 'opacity .6s ease 1.2s' }}>
      {AI_CAPABILITIES.map((cap, i) => (
        <div key={i} className="dist-ai-item" style={{
          padding: '16px 16px', background: 'rgba(0,255,170,.05)',
          border: '1px solid rgba(0,255,170,.12)', borderRadius: 12,
          transition: 'all .2s',
        }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#00ffaa', marginTop: 5, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 5, fontFamily: "'DM Sans', sans-serif" }}>{cap.name}</div>
              <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.55, fontFamily: "'DM Sans', sans-serif" }}>{cap.desc}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function CTAContent({ visible, navigate }) {
  return (
    <div style={{ opacity: visible ? 1 : 0, transition: 'opacity .6s ease 1.4s' }}>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', margin: '24px 0 28px' }}>
        {CHANNELS.map((ch, i) => (
          <div key={i} style={{
            padding: '8px 16px', borderRadius: 20, fontSize: 12, fontWeight: 600,
            background: `${ch.color}12`, border: `1px solid ${ch.color}30`, color: ch.color,
            fontFamily: "'DM Sans', sans-serif",
          }}>{ch.label}</div>
        ))}
      </div>
      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: '#334155', marginBottom: 24, letterSpacing: '1px' }}>
        PILOMATION AI · DISTRIBUTION ENGINE · ALWAYS ON
      </div>
      <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button className="dist-btn-p" onClick={() => navigate('/signup')} style={{
          padding: '14px 32px', background: '#00ffaa', color: '#001a0f',
          border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700,
          cursor: 'pointer', transition: 'transform .2s, box-shadow .2s',
          fontFamily: "'DM Sans', sans-serif", letterSpacing: '.3px',
        }}>Distribute your film →</button>
        <button className="dist-btn-g" onClick={() => navigate('/distributions')} style={{
          padding: '14px 32px', background: 'transparent', color: '#94a3b8',
          border: '1px solid rgba(255,255,255,.15)', borderRadius: 10, fontSize: 14,
          cursor: 'pointer', transition: 'all .2s', fontFamily: "'DM Sans', sans-serif",
        }}>Explore network</button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const DistributionGuide = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(false);
  const [progKey, setProgKey] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => { injectStyles(); setVisible(true); }, []);

  const goTo = useCallback((n) => {
    const next = ((n % SLIDES.length) + SLIDES.length) % SLIDES.length;
    setVisible(false);
    setTimeout(() => { setCurrent(next); setProgKey(k => k + 1); setVisible(true); }, 280);
  }, []);

  useEffect(() => {
    if (paused) { clearTimeout(timerRef.current); return; }
    timerRef.current = setTimeout(() => goTo(current + 1), DURATION);
    return () => clearTimeout(timerRef.current);
  }, [current, paused, goTo]);

  const slide = SLIDES[current];

  return (
    <div className="pb-dist" style={{ minHeight: '100vh', background: '#020b06', color: '#e8edf2', display: 'flex', flexDirection: 'column' }}>
      <SEO
        title="Film Distribution Guide"
        description="Learn how to distribute your film across streaming platforms, theatrical releases, and digital markets. ProducerBuddy guides you step by step through the distribution process."
        url="https://producerbuddy.pilomation.com/distribution-guide"
      />
      <Nav />

      {!paused && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 3, zIndex: 200, background: 'rgba(0,255,170,.08)' }}>
          <div key={progKey} className="dist-prog-bar" style={{
            height: '100%',
            background: `linear-gradient(90deg, ${slide.accent}, ${slide.accent}88)`,
            animationDuration: `${DURATION}ms`, animationTimingFunction: 'linear', animationFillMode: 'both',
          }} />
        </div>
      )}

      <div style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, background: slide.bg, transition: 'background .8s', zIndex: 0 }} />

        <div className={visible ? 'dist-slide-enter' : ''} style={{
          position: 'relative', zIndex: 2, maxWidth: 940, width: '100%',
          padding: '44px 36px', textAlign: 'center', opacity: visible ? 1 : 0,
        }}>
          <div style={{
            fontSize: 10, letterSpacing: '3.5px', textTransform: 'uppercase', color: slide.accent,
            marginBottom: 18, fontFamily: "'Space Mono', monospace",
            opacity: visible ? 1 : 0, transition: 'opacity .5s ease .2s',
          }}>{slide.kicker}</div>

          <Headline text={slide.headline} active={visible} accent={slide.accent} />

          {slide.body && (
            <p style={{
              fontSize: 15, lineHeight: 1.8, color: '#94a3b8', maxWidth: 580,
              margin: '0 auto 8px', fontFamily: "'DM Sans', sans-serif",
              opacity: visible ? 1 : 0, transition: 'opacity .6s ease 1.2s',
            }}>{slide.body}</p>
          )}

          {slide.type === 'globe'    && <GlobeSlide visible={visible} />}
          {slide.type === 'history'  && <HistoryContent visible={visible} />}
          {slide.type === 'channel'  && <ChannelContent slide={slide} visible={visible} />}
          {slide.type === 'marketer' && <MarketerContent visible={visible} />}
          {slide.type === 'ai'       && <AIContent visible={visible} />}
          {slide.type === 'cta'      && <CTAContent visible={visible} navigate={navigate} />}
        </div>
      </div>

      <div style={{ padding: '16px 0 14px', background: 'rgba(2,11,6,.95)', backdropFilter: 'blur(12px)', zIndex: 10 }}>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 12 }}>
          {SLIDES.map((_, i) => (
            <div key={i} className={i === current ? 'dist-dot-active' : ''} onClick={() => goTo(i)} style={{
              width: 6, height: 6, borderRadius: '50%',
              background: 'rgba(0,255,170,.2)', cursor: 'pointer', transition: 'all .3s',
            }} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          {['← prev', paused ? '▶ play' : '⏸ pause', 'next →'].map((label, i) => (
            <button key={i} className="dist-ctrl" onClick={() => {
              if (i === 0) goTo(current - 1);
              else if (i === 1) setPaused(p => !p);
              else goTo(current + 1);
            }} style={{
              background: 'rgba(0,255,170,.05)', border: '1px solid rgba(0,255,170,.15)',
              color: '#94a3b8', padding: '8px 20px', borderRadius: 6, fontSize: 12,
              cursor: 'pointer', transition: 'all .2s', fontFamily: "'Space Mono', monospace",
            }}>{label}</button>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 10, fontSize: 10, color: '#0a2e1a', fontFamily: "'Space Mono', monospace", letterSpacing: '1.5px' }}>
          {String(current + 1).padStart(2, '0')} / {String(SLIDES.length).padStart(2, '0')} · {slide.kicker.toUpperCase()}
        </div>
      </div>
    </div>
  );
};

export default DistributionGuide;
